import React, { useState, useEffect, useRef } from 'react';
import { Facebook, Instagram, Youtube, Linkedin, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const footerRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (footerRef.current) observer.observe(footerRef.current);
    return () => { if (footerRef.current) observer.unobserve(footerRef.current); };
  }, []);

  const XIcon = () => (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.53 3H21L14.19 10.68L22.14 21H15.97L10.98 14.37L5.36 21H2L9.13 13.01L1.43 3H7.77L12.31 9.09L17.53 3ZM16.41 19H18.22L7.7 5H5.76L16.41 19Z"/>
    </svg>
  );

  const footerLinks = {
    company: [
      { name: 'Home',           to: '/' },
      { name: 'About us',       to: '/about-us' },
      { name: 'Leadership',     to: '/leadership' },
      { name: 'Events',         to: '/events' },
      { name: 'KYP',            to: '/products' },
      { name: 'Opportunity',    to: '/opportunity' },
      { name: 'Statutory',      to: '/statutory' },
      { name: 'Virtual office', to: 'https://in.indusviva.com/signin', external: true },
    ],
    social: [
      { name: 'Facebook',  href: 'https://www.facebook.com/media/set/?vanity=officialVibrantViva&set=a.705168821617957', Icon: Facebook },
      { name: 'Instagram', href: 'https://www.instagram.com/vibrantvivaofficial/',                                       Icon: Instagram },
      { name: 'Youtube',   href: 'https://www.youtube.com/@VibrantViva',                                                 Icon: Youtube },
      { name: 'LinkedIn',  href: 'https://www.linkedin.com/company/indusviva-healthsciences-private-limited/posts/?feedView=all&viewAsMember=true', Icon: Linkedin },
      { name: 'Twitter',   href: 'https://x.com/IndusvivaPvt?t=yf_P18W0HW6DR9ZbrhjRzg&s=09', Icon: XIcon },
    ],
  };

  const AnimatedText = ({ text, delay = 0 }) => (
    <span className="inline-block">
      {text.split('').map((char, index) => (
        <span
          key={index}
          className={`inline-block animated-char ${isVisible ? 'visible' : ''}`}
          style={{ animationDelay: `${delay + index * 0.05}s`, opacity: 0 }}
        >
          {char === ' ' ? '\u00A0' : char}
        </span>
      ))}
    </span>
  );

  return (
    <footer
      ref={footerRef}
      className="footer-section relative overflow-hidden"
      style={{
        background: 'linear-gradient(to bottom, #2D5E82 0%, #1A3A5C 30%, #1A3A5C 65%, #2D5E82 100%)',
      }}
    >
      {/* Curved accent shape */}
      <div className="absolute top-0 right-0 w-1/3 h-full pointer-events-none">
        <svg viewBox="0 0 500 800" className="absolute top-0 right-0 h-full w-auto" preserveAspectRatio="none">
          <path d="M 500,0 L 500,800 L 200,800 Q 100,400 200,0 Z" fill="#17a2b8" opacity="0.08" />
        </svg>
      </div>

      <div className="relative z-10">

        {/* ── CTA Section ── */}
        <div className="container mx-auto px-4 lg:px-8 pt-20 pb-20">
          <div className="flex flex-col items-center text-center gap-10">

            <h2
              className="footer-cta-heading text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white w-full"
              style={{ lineHeight: 1.25 }}
            >
              <div><AnimatedText text="Let's Build the" delay={0} /></div>
              <div className="mt-2"><AnimatedText text="Future Together" delay={0.5} /></div>
            </h2>

            <div className={`transition-all duration-700 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <p className="footer-tagline-pill">
                Vibrant Viva is committed to building one of India's most transparent and ethical direct selling platforms.
              </p>
            </div>

          </div>
        </div>

        {/* ── Footer Content — 3 columns ── */}
        <div className="border-t border-white/10">
          <div className="container mx-auto px-4 lg:pl-16 lg:pr-8 xl:pl-28 xl:pr-12 2xl:pl-36 2xl:pr-16 py-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">

              {/* Company Links */}
              <div>
                <h3 className="footer-col-heading text-xl font-bold text-white mb-6">Company</h3>
                <ul className="space-y-3">
                  {footerLinks.company.map((link, index) => (
                    <li key={index}>
                      {link.external ? (
                        <a href={link.to} target="_blank" rel="noopener noreferrer"
                          className="footer-link text-white/70 hover:text-white transition-colors duration-300 inline-block hover:translate-x-1 transform">
                          {link.name}
                        </a>
                      ) : (
                        <a href={link.to}
                          className="footer-link text-white/70 hover:text-white transition-colors duration-300 inline-block hover:translate-x-1 transform">
                          {link.name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="footer-col-heading text-xl font-bold text-white mb-6">Social Links</h3>
                <ul className="space-y-4">
                  {footerLinks.social.map(({ name, href, Icon }, index) => (
                    <li key={index}>
                      <a href={href} target="_blank" rel="noopener noreferrer"
                        className="footer-link flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-300 group">
                        <span className="w-8 h-8 bg-white/10 border border-white/15 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:bg-white/25 group-hover:border-white/30">
                          <Icon />
                        </span>
                        <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">{name}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="footer-col-heading text-xl font-bold text-white mb-6">Contact Info</h3>
                <ul className="space-y-4">
                  <li className="flex gap-3 text-white/70">
                    <MapPin className="w-5 h-5 flex-shrink-0 text-white mt-1" />
                    <span className="footer-contact-text text-sm leading-relaxed">
                      <strong className="text-white block mb-1">Viva Tower</strong>
                      No 92-1-36, Viva Tower<br />
                      Nandi Durga Road<br />
                      Jayamahal Extension<br />
                      Bangalore Urban, Karnataka - 560046<br />
                      India
                    </span>
                  </li>
                 
                  <li className="flex gap-3 text-white/70">
                    <Phone className="w-5 h-5 flex-shrink-0 text-white" />
                    <div>
                      <a href="tel:18001034916" className="footer-link hover:text-white transition-colors block">1800 103 4916</a>
                      <span className="footer-contact-text text-sm"> Mon–Fri: 9:30am – 6:00pm</span><br />
                      <span className="footer-contact-text text-sm"> Sat: 9:30am – 1:30pm</span>
                    </div>
                  </li>
                </ul>
              </div>

            </div>
          </div>
        </div>

        {/* ── Disclaimer ── */}
        <div className="border-t border-white/20" style={{ background: 'rgba(10,20,40,0.55)', backdropFilter: 'blur(8px)' }}>
          <div className="container mx-auto px-4 lg:px-8 py-5">
            <p className="footer-disclaimer-heading text-white text-xs font-semibold uppercase tracking-widest mb-1">Disclaimer</p>
            <p className="footer-disclaimer text-xs leading-relaxed">
              This platform is for individuals interested in becoming independent direct sellers. This is not a job, employment or recruitment portal.
            </p>
          </div>
        </div>

        {/* ── Bottom Bar ── */}
        <div className="border-t border-white/10" style={{ background: 'rgba(8,15,30,0.65)', backdropFilter: 'blur(8px)' }}>
          <div className="container mx-auto px-4 lg:px-8 py-6">
            <p className="footer-copyright text-white/50 text-sm text-center w-full">
              © 2026 <strong className="text-white">VibrantViva</strong> — All rights reserved.
            </p>
          </div>
        </div>

      </div>

       <style dangerouslySetInnerHTML={{ __html:`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Outfit:wght@300;400;500;600;700&display=swap');

        .footer-section, .footer-section *, .footer-section *::before, .footer-section *::after {
          font-family: 'Outfit', sans-serif;
        }

        .footer-cta-heading, .footer-cta-heading span {
          font-family: 'Cormorant Garamond', serif !important;
          font-weight: 600 !important;
        }

        .footer-col-heading {
          font-family: 'Cormorant Garamond', serif !important;
          font-weight: 600 !important;
        }

        .footer-link, .footer-contact-text, .footer-copyright {
          font-family: 'Outfit', sans-serif !important;
          font-weight: 400 !important;
        }

        .footer-disclaimer-heading {
          font-family: 'Outfit', sans-serif !important;
          font-weight: 600 !important;
        }

        .footer-disclaimer {
          font-family: 'Outfit', sans-serif !important;
          font-weight: 400 !important;
          color: rgba(255,255,255,0.65) !important;
        }

        .footer-tagline-pill {
          display: inline-block;
          font-family: 'Outfit', sans-serif !important;
          font-size: 20px;
          font-weight: 500;
          color: rgba(255,255,255,0.95);
          background: rgba(255,255,255,0.10);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.25);
          padding: 18px 48px;
          border-radius: 999px;
          text-align: center;
          white-space: nowrap;
          line-height: 1.6;
          letter-spacing: 0.02em;
        }

        @media (max-width: 1024px) {
          .footer-tagline-pill { font-size: 17px; padding: 16px 32px; white-space: normal; }
        }
        @media (max-width: 768px) {
          .footer-tagline-pill { font-size: 14px; padding: 14px 20px; white-space: normal; }
        }

        @keyframes charFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animated-char.visible {
          animation: charFadeIn 0.8s cubic-bezier(0.25,0.46,0.45,0.94) forwards;
        }

        a { transition: all 0.3s ease; }
      `}}/>
    </footer>
  );
};

export default Footer;